import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ImageSearch as ImageSearchIcon,
  Link as LinkIcon,
  Psychology as PsychologyIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { FailureMode, UsedPrecheckAnswer } from '../../types/drbfm';

interface DRBFMDecisionMemoryPanelProps {
  failureMode: FailureMode | null;
  onOpenDrawingEvidence: (failureMode: FailureMode) => void;
}

const formatPercent = (value: number | undefined): string => (
  `${Math.round((value || 0) * 100)}%`
);

const getSeverityLabel = (failureMode: FailureMode): string => (
  `${failureMode.severity} / ${failureMode.severityScore || '-'}`
);

const buildReviewComment = (
  failureMode: FailureMode,
  t: ReturnType<typeof useTranslation>['t'],
): string => {
  const evidenceText = failureMode.knowledgeReferences.length > 0
    ? t('drbfm.decisionMemory.knowledgeLinked', '関連知識と照合済み')
    : t('drbfm.decisionMemory.knowledgeUnlinked', '関連知識は未紐付け');
  const questionText = failureMode.reviewQuestions?.length
    ? t('drbfm.decisionMemory.additionalReviewNeeded', '追加確認が必要')
    : t('drbfm.decisionMemory.noAdditionalQuestions', '追加質問なし');
  return t('drbfm.decisionMemory.reviewComment', {
    description: failureMode.failureDescription,
    rpn: failureMode.rpnScore || '-',
    evidenceText,
    questionText,
    defaultValue: `${failureMode.failureDescription}はRPN ${failureMode.rpnScore || '-'}。${evidenceText}、${questionText}としてレビューしてください。`,
  });
};

const getPrecheckAnswerChipLabel = (
  answer: UsedPrecheckAnswer,
  t: ReturnType<typeof useTranslation>['t'],
): string => {
  switch (answer.status) {
    case 'answered':
      return answer.answer || answer.answerText || answer.answer_text || t('drbfm.case.answerStatus.answered', '回答済み');
    case 'unknown':
      return t('drbfm.case.answerStatus.unknown', '不明');
    case 'not_applicable':
      return t('drbfm.case.answerStatus.not_applicable', '該当なし');
    default:
      return t('drbfm.case.answerStatus.unanswered', '未回答');
  }
};

const DRBFMDecisionMemoryPanel: React.FC<DRBFMDecisionMemoryPanelProps> = ({
  failureMode,
  onOpenDrawingEvidence,
}) => {
  const { t } = useTranslation();

  if (!failureMode) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t('drbfm.decisionMemory.title', '設計判断メモリ')}
        </Typography>
        <Alert severity="info">{t('drbfm.decisionMemory.selectFailureMode', '故障モードを選択してください。')}</Alert>
      </Paper>
    );
  }

  const hasKnowledge = failureMode.knowledgeReferences.length > 0;
  const hasDrawingEvidence = Boolean(failureMode.drawingEvidence);
  const hasQuestions = Boolean(failureMode.reviewQuestions?.length);
  const hasUsedAnswers = Boolean(failureMode.usedPrecheckAnswers?.length);
  const hasUnknownAssumptions = Boolean(failureMode.unknownAssumptions?.length);

  return (
    <Paper variant="outlined" sx={{ p: 2, position: 'sticky', top: 16 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {t('drbfm.decisionMemory.title', '設計判断メモリ')}
          </Typography>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.6 }}>
            {failureMode.failureDescription}
          </Typography>
          {failureMode.failureCause && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
              {failureMode.failureCause}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip
            label={t('drbfm.decisionMemory.severity', {
              value: getSeverityLabel(failureMode),
              defaultValue: `重大度 ${getSeverityLabel(failureMode)}`,
            })}
            size="small"
            color="warning"
          />
          <Chip label={`RPN ${failureMode.rpnScore || '-'}`} size="small" color="error" />
          {hasKnowledge && <Chip label={t('drbfm.decisionMemory.evidenceAvailable', '根拠あり')} size="small" icon={<LinkIcon />} />}
          {hasDrawingEvidence && <Chip label={t('drbfm.decisionMemory.drawingLinked', '図面リンク済み')} size="small" color="info" />}
          {hasQuestions && <Chip label={t('drbfm.decisionMemory.questionsGenerated', '質問生成')} size="small" color="secondary" />}
        </Stack>

        <Box
          sx={{
            p: 1.5,
            bgcolor: 'grey.50',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <PsychologyIcon color="primary" fontSize="small" />
            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
              {buildReviewComment(failureMode, t)}
            </Typography>
          </Stack>
        </Box>

        {hasQuestions && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('drbfm.decisionMemory.followUpQuestions', 'AI追加確認質問')}
            </Typography>
            <Stack spacing={1}>
              {failureMode.reviewQuestions!.map((question) => (
                <Box
                  key={question.id}
                  sx={{
                    p: 1.25,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <QuestionAnswerIcon fontSize="small" color="secondary" />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.6 }}>
                        {question.question}
                      </Typography>
                      {question.reason && (
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {question.reason}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {hasUsedAnswers && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('drbfm.decisionMemory.usedPrecheckAnswers', '事前確認で使った回答')}
            </Typography>
            <Stack spacing={1}>
              {failureMode.usedPrecheckAnswers!.map((answer, index) => (
                <Box
                  key={answer.id || answer.requiredContextKey || answer.question || index}
                  sx={{
                    p: 1.25,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Stack spacing={1}>
                    {answer.question && (
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.6 }}>
                        {answer.question}
                      </Typography>
                    )}
                    <Box>
                      <Chip
                        label={getPrecheckAnswerChipLabel(answer, t)}
                        size="small"
                        color={answer.status === 'unknown' ? 'warning' : 'default'}
                        sx={{ maxWidth: '100%' }}
                      />
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {hasUnknownAssumptions && (
          <Alert severity="warning" sx={{ py: 1 }}>
            <AlertTitle sx={{ mb: 0.5 }}>
              {t('drbfm.decisionMemory.unresolvedAssumptions', '未確定の前提')}
            </AlertTitle>
            <Box component="ul" sx={{ pl: 2, my: 0 }}>
              {failureMode.unknownAssumptions!.map((assumption, index) => (
                <Typography
                  key={`${assumption}-${index}`}
                  component="li"
                  variant="body2"
                  sx={{ lineHeight: 1.6 }}
                >
                  {assumption}
                </Typography>
              ))}
            </Box>
          </Alert>
        )}

        {hasKnowledge && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('drbfm.decisionMemory.relatedKnowledgeSummary', '関連知識サマリー')}
            </Typography>
            <Stack spacing={0.75}>
              {failureMode.knowledgeReferences.slice(0, 3).map((reference) => (
                <Box key={`${reference.documentId}-${reference.title}`}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
                    {reference.fileName || reference.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('drbfm.decisionMemory.relevance', {
                      value: formatPercent(reference.relevanceScore),
                      defaultValue: `関連度 ${formatPercent(reference.relevanceScore)}`,
                    })}
                    {reference.pageNumber ? ` / p.${reference.pageNumber}` : ''}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {failureMode.drawingEvidence && (
          <>
            <Divider />
            <Box
              sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {failureMode.drawingEvidence.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('drbfm.decisionMemory.confidence', {
                  value: formatPercent(failureMode.drawingEvidence.confidence),
                  defaultValue: `信頼度 ${formatPercent(failureMode.drawingEvidence.confidence)}`,
                })}
              </Typography>
              {failureMode.drawingEvidence.reasoning && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
                  {failureMode.drawingEvidence.reasoning}
                </Typography>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<ImageSearchIcon />}
                sx={{ mt: 1.5 }}
                onClick={() => onOpenDrawingEvidence(failureMode)}
              >
                {t('drbfm.decisionMemory.confirmInDrawing', '図面で確認')}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default DRBFMDecisionMemoryPanel;
